import {ButtonProps, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  AddBasketItemBody,
  ArticleUpdateJobItem,
  useCheckoutArticleUpdateJobBasketMutation,
  useDeleteArticleUpdateJobItemsMutation,
  useDiscardArticleUpdateJobBasketMutation,
  useGetArticleUpdateJobBasketQuery,
  usePostArticleUpdateJobBasketItemsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  FullScreenModal,
  WAREHOUSE_GRID_CODES,
  handleApiError,
  refreshDmsDatagrid,
} from '@dms/shared';
import {AfterSalesPlainMaterialBasket} from '@dms/shared-aftersales';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ArticleUpdateJobMaterialModalList} from './ArticleUpdateJobMaterialModalList';

interface ArticleUpdateJobMaterialModalProps extends RequiredTestIdProps {
  articleUpdateJobId: string;
  onClose: VoidFunction;
}

export function ArticleUpdateJobMaterialModal(props: ArticleUpdateJobMaterialModalProps) {
  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetArticleUpdateJobBasketQuery({articleUpdateJobId: props.articleUpdateJobId});

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    useCheckoutArticleUpdateJobBasketMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] =
    useDiscardArticleUpdateJobBasketMutation();

  const [addItemsToBasket, {isLoading: isAddingItemsToBasket}] =
    usePostArticleUpdateJobBasketItemsMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeleteArticleUpdateJobItemsMutation();

  const basketItems = basket?.articleUpdateJobItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const hasPendingRequest =
    isRefreshingBasket ||
    isCheckingOutBasket ||
    isDiscardingBasket ||
    isAddingItemsToBasket ||
    isDeletingBasketItems;

  const handleCheckout = () =>
    checkoutBasket({articleUpdateJobId: props.articleUpdateJobId})
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.materialAdded')))
      .then(props.onClose)
      .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.articleUpdateJobItems))
      .catch(handleApiError);

  const handleDiscard = () =>
    discardBasket({articleUpdateJobId: props.articleUpdateJobId})
      .unwrap()
      .then(props.onClose)
      .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.articleUpdateJobItems))
      .catch(handleApiError);

  const handleAddItemsToBasket = async (items: AddBasketItemBody) => {
    await addItemsToBasket({articleUpdateJobId: props.articleUpdateJobId, body: items})
      .unwrap()
      .then(() =>
        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.articleUpdateJobBasketItems, {deselectAll: true})
      )
      .catch(handleApiError);
  };

  const handleDeleteBasketItems = async (itemsIds: string[]) => {
    await deleteBasketItems({
      articleUpdateJobId: props.articleUpdateJobId,
      body: {articleUpdateJobItemId: itemsIds},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
      'data-testid': suffixTestId('headerActions.discard', props),
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
      'data-testid': suffixTestId('headerActions.add', props),
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.actions.addMaterial')} actions={actions}>
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ArticleUpdateJobMaterialModalList
              onAddItemsToBasket={handleAddItemsToBasket}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesPlainMaterialBasket<ArticleUpdateJobItem>
              basket={{
                items: basketItems,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                isDeletingItems: isDeletingBasketItems,
              }}
              onDelete={handleDeleteBasketItems}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
